/**
 * Iter 97 — dedicated Today Operations screen (spec §6). The fastest mobile
 * screen: tabs All | Pickups | Returns | Active with big Start Pickup /
 * Complete Return buttons that launch the step-by-step wizards.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import Header from "@/components/Header";
import Money from "@/components/Money";
import OpsBottomNav from "@/components/ops/OpsBottomNav";
import BookingStatusBadge from "@/components/ops/BookingStatusBadge";
import PickupWizard from "@/components/ops/PickupWizard";
import ReturnWizard from "@/components/ops/ReturnWizard";
import SwipeableRow from "@/components/ops/SwipeableRow";
import PaymentsSheet from "@/components/ops/PaymentsSheet";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Phone, MessageCircle, MapPin, KeyRound, Undo2, RefreshCw, FileSignature, MoreHorizontal, Wallet, FileDown, Eye } from "lucide-react";

const TABS = [
  { key: "all", label: "All" },
  { key: "pickups", label: "Pickups" },
  { key: "returns", label: "Returns" },
  { key: "active", label: "Active" },
];

function timeOf(iso) {
  const s = String(iso || "");
  return s.length > 10 ? s.slice(11, 16) : null;
}

/** Iter 102 — vendor daily cash reconciliation summary. */
function CollectionsToday() {
  const [c, setC] = useState(null);
  const [shift, setShift] = useState(null);
  const [showClose, setShowClose] = useState(false);
  const [actual, setActual] = useState("");
  const [note, setNote] = useState("");
  const [closing, setClosing] = useState(false);

  const loadShift = useCallback(() => {
    api.get("/vendor/cash-shift/today").then((r) => setShift(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/vendor/collections/today").then((r) => setC(r.data)).catch(() => {});
    loadShift();
  }, [loadShift]);

  const expected = Number(shift?.expected_cash || 0);
  const diff = actual === "" ? null : Number(actual) - expected;

  const closeShift = async () => {
    if (actual === "" || Number(actual) < 0) { toast.error("Enter the counted cash amount"); return; }
    setClosing(true);
    try {
      await api.post("/vendor/cash-shift/close", { actual_cash: Number(actual), note: note || null });
      toast.success("Cash shift closed");
      setShowClose(false); setActual(""); setNote("");
      loadShift();
    } catch (e) { toast.error(formatApiError(e)); } finally { setClosing(false); }
  };

  const hasShifts = (shift?.shifts || []).length > 0;
  if ((!c || c.total === 0) && expected === 0 && !hasShifts) return null;
  return (
    <div className="mb-4 rounded-3xl border border-emerald-200 bg-emerald-50/50 p-4" data-testid="collections-today">
      <div className="text-[11px] uppercase tracking-wide font-bold text-emerald-700 mb-1.5">Today's collections</div>
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-2xl font-display font-bold" data-testid="collections-total"><Money amount={c?.total || 0} /></span>
        {c?.deposits > 0 && <span className="text-xs text-neutral-500">incl. deposits <Money amount={c.deposits} /></span>}
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {Object.entries(c?.by_method || {}).map(([m, v]) => (
          <span key={m} className="px-2 py-0.5 rounded-md bg-white border border-border text-[11px] font-bold">{m}: <Money amount={v} /></span>
        ))}
      </div>
      {Object.keys(c?.by_staff || {}).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {Object.entries(c.by_staff).map(([s, v]) => (
            <span key={s} className="px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-800 text-[11px] font-bold">{s}: <Money amount={v} /></span>
          ))}
        </div>
      )}

      {/* Iter 103 — close cash shift: staff counts the drawer, we show the delta. */}
      <div className="mt-3 pt-3 border-t border-emerald-200/70">
        {shift?.my_shift ? (
          <div className="text-sm" data-testid="cash-shift-closed">
            <span className="font-bold">Shift closed</span> — expected <Money amount={shift.my_shift.expected_cash} />, counted <Money amount={shift.my_shift.actual_cash} />
            <span className={`ml-1.5 font-bold ${shift.my_shift.difference === 0 ? "text-emerald-700" : "text-rose-600"}`}>
              ({shift.my_shift.difference > 0 ? "+" : ""}<Money amount={shift.my_shift.difference} />)
            </span>
          </div>
        ) : !showClose ? (
          <button type="button" onClick={() => { setShowClose(true); setActual(""); }} className="w-full rounded-xl border border-emerald-300 bg-white text-emerald-800 text-sm font-bold py-2.5" data-testid="close-cash-shift-btn">
            Close cash shift — expected cash <Money amount={expected} />
          </button>
        ) : (
          <div className="space-y-2" data-testid="cash-shift-form">
            <div className="text-xs text-neutral-600">Expected cash you collected today: <span className="font-bold"><Money amount={expected} /></span>. Count the drawer and enter the actual amount:</div>
            <input type="number" inputMode="decimal" value={actual} onChange={(e) => setActual(e.target.value)} placeholder="Actual cash counted (€)" className="w-full rounded-xl border border-border px-3 py-2.5 text-sm" data-testid="cash-shift-actual" />
            {diff != null && !Number.isNaN(diff) && (
              <div className={`text-sm font-bold ${diff === 0 ? "text-emerald-700" : "text-rose-600"}`} data-testid="cash-shift-diff">
                {diff === 0 ? "✓ Drawer matches — no difference" : diff > 0 ? <>Over by <Money amount={diff} /></> : <>Short by <Money amount={Math.abs(diff)} /></>}
              </div>
            )}
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className="w-full rounded-xl border border-border px-3 py-2.5 text-sm" data-testid="cash-shift-note" />
            <div className="flex gap-2">
              <button type="button" onClick={closeShift} disabled={closing} className="flex-1 rounded-xl bg-emerald-600 text-white text-sm font-bold py-2.5 disabled:opacity-60" data-testid="cash-shift-submit">
                {closing ? "Closing…" : "Close shift"}
              </button>
              <button type="button" onClick={() => setShowClose(false)} className="rounded-xl border border-border px-4 text-sm font-bold" data-testid="cash-shift-cancel">Cancel</button>
            </div>
          </div>
        )}
        {hasShifts && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {shift.shifts.map((s) => (
              <span key={s.id} className={`px-2 py-0.5 rounded-md text-[11px] font-bold ${s.difference === 0 ? "bg-white border border-border text-neutral-700" : "bg-rose-50 border border-rose-200 text-rose-700"}`} data-testid={`cash-shift-row-${s.id}`}>
                {s.staff_name}: {s.difference === 0 ? "balanced" : `${s.difference > 0 ? "+" : ""}€${Math.abs(s.difference).toFixed(2)}${s.difference < 0 ? " short" : " over"}`}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PayChip({ b }) {
  const due = Number(b.amount_due_at_pickup || 0);
  if (b.payment_status === "paid" && due <= 0) {
    return <span className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[11px] font-bold">Paid in full</span>;
  }
  if (due > 0) {
    return <span className="px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 text-[11px] font-bold">Due at pickup: <Money amount={due} /></span>;
  }
  return <span className="px-2 py-0.5 rounded-md bg-neutral-100 text-neutral-600 text-[11px] font-bold">{b.payment_status || "—"}</span>;
}

export default function TodayOps() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("all");
  const [pickupTarget, setPickupTarget] = useState(null);
  const [returnTarget, setReturnTarget] = useState(null);
  const [moreTarget, setMoreTarget] = useState(null);
  const [payTarget, setPayTarget] = useState(null);

  const role = user?.role === "admin" || user?.role === "agent" ? "admin" : "vendor";
  const dashPath = role === "admin" ? "/dashboard/admin" : "/dashboard/vendor";

  const load = useCallback(() => {
    api.get("/ops/today").then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!user) return null;

  const items = (data?.items || []).filter((b) => tab === "all" || (b.buckets || []).includes(tab));

  const handleBottomNav = (key) => {
    if (key === "today-ops") return;
    if (key === "agreements") { navigate("/dashboard/agreements"); return; }
    if (role === "admin") {
      navigate(`/dashboard/admin#${key === "calendar" || key === "bookings" ? "bookings" : key === "dashboard" || key === "operations" ? "operations" : key}`);
    } else {
      navigate(key === "dashboard" ? "/dashboard/vendor" : `/dashboard/vendor?tab=${key}`);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <Header />
      <div className="max-w-3xl mx-auto px-4 pt-6 pb-24" data-testid="today-ops-page">
        <div className="flex items-center justify-between gap-3 mb-1">
          <h1 className="font-display text-3xl tracking-tight font-medium">Today</h1>
          <button
            type="button"
            onClick={load}
            className="h-9 w-9 rounded-full border border-border bg-white flex items-center justify-center text-neutral-500"
            data-testid="today-refresh"
            aria-label="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
        <div className="text-sm text-neutral-500 mb-4">{data?.date ? `${data.date} · daily operations` : "daily operations"}</div>
        <CollectionsToday />

        <div className="flex gap-2 mb-4 overflow-x-auto pb-1" data-testid="today-tabs">
          {TABS.map((t) => {
            const n = t.key === "all" ? (data?.items || []).length : (data?.items || []).filter((b) => (b.buckets || []).includes(t.key)).length;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`shrink-0 px-4 py-2 rounded-full text-sm font-bold border transition ${
                  tab === t.key ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-600 border-border"
                }`}
                data-testid={`today-tab-${t.key}`}
              >
                {t.label} {n > 0 && <span className="ml-1 opacity-70">{n}</span>}
              </button>
            );
          })}
        </div>

        {!data && <div className="text-center text-neutral-400 py-16">Loading…</div>}
        {data && items.length === 0 && (
          <div className="rounded-3xl border border-dashed border-border bg-white p-10 text-center text-neutral-500" data-testid="today-empty">
            Nothing scheduled here today. Enjoy the calm ☕
          </div>
        )}

        <div className="space-y-3">
          {items.map((b) => {
            const overdue = (b.buckets || []).includes("overdue");
            const isPickup = (b.buckets || []).includes("pickups") && !b.picked_up_at;
            const isReturn = !isPickup && !b.returned_at &&
              ((b.buckets || []).includes("returns") || overdue || (b.buckets || []).includes("active"));
            const wa = String(b.customer?.whatsapp || "").replace(/[^\d+]/g, "");
            const primary = () => (isPickup ? setPickupTarget(b) : isReturn ? setReturnTarget(b) : navigate(`/agreements/${b.id}`));
            return (
              <SwipeableRow
                key={b.id}
                onSwipeRight={primary}
                onSwipeLeft={() => setMoreTarget(b)}
                rightLabel={isPickup ? "Start Pickup" : isReturn ? "Complete Return" : "View"}
                leftLabel="More"
                testId={`swipe-${b.id}`}
              >
              <div
                className={`rounded-3xl border bg-white p-4 ${overdue ? "border-rose-300 ring-2 ring-rose-100" : "border-border"}`}
                data-testid={`today-card-${b.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                <button type="button" onClick={() => navigate(`/agreements/${b.id}`)} className="min-w-0 text-left flex-1" data-testid={`today-open-${b.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-[11px] font-bold text-neutral-400">#{(b.id || "").slice(0, 8).toUpperCase()}</span>
                      <BookingStatusBadge booking={b} />
                    </div>
                    <div className="font-bold mt-1 truncate">{b.car?.make} {b.car?.model}{b.car?.license_plate ? ` · ${b.car.license_plate}` : ""}</div>
                    <div className="text-sm text-neutral-600 truncate">{b.customer?.name || "—"}</div>
                </button>
                  <div className="text-right shrink-0">
                    <div className="text-lg font-display font-bold">{timeOf(isPickup ? b.start_date : b.end_date) || "—"}</div>
                    <div className="text-[10px] uppercase tracking-wide text-neutral-400 font-bold">{isPickup ? "pickup" : "return"}</div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 mt-2 text-xs text-neutral-500">
                  {b.pickup_location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{b.pickup_location}</span>}
                  <span>· {String(b.start_date || "").slice(0, 10)} → {String(b.end_date || "").slice(0, 10)}</span>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  <PayChip b={b} />
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold ${b.agreement?.pickup_signed ? "bg-emerald-50 text-emerald-700" : "bg-neutral-100 text-neutral-500"}`}>
                    <FileSignature className="h-3 w-3" /> {b.agreement?.pickup_signed ? "Agreement signed" : "Agreement unsigned"}
                  </span>
                </div>
                <div className="grid grid-cols-5 gap-2 mt-3 pt-3 border-t border-border">
                  {isPickup && (
                    <button
                      type="button"
                      onClick={() => setPickupTarget(b)}
                      className="col-span-2 inline-flex items-center justify-center gap-1.5 rounded-xl bg-emerald-600 text-white text-sm font-bold py-3 active:scale-[0.98] transition"
                      data-testid={`start-pickup-${b.id}`}
                    >
                      <KeyRound className="h-4 w-4" /> Start Pickup
                    </button>
                  )}
                  {isReturn && !isPickup && (
                    <button
                      type="button"
                      onClick={() => setReturnTarget(b)}
                      className="col-span-2 inline-flex items-center justify-center gap-1.5 rounded-xl bg-blue-600 text-white text-sm font-bold py-3 active:scale-[0.98] transition"
                      data-testid={`complete-return-${b.id}`}
                    >
                      <Undo2 className="h-4 w-4" /> Complete Return
                    </button>
                  )}
                  {!isPickup && !isReturn && (
                    <button
                      type="button"
                      onClick={() => navigate(`/agreements/${b.id}`)}
                      className="col-span-2 inline-flex items-center justify-center rounded-xl bg-neutral-900 text-white text-sm font-bold py-3"
                      data-testid={`today-view-${b.id}`}
                    >
                      View reservation
                    </button>
                  )}
                  {wa ? (
                    <>
                      <a href={`tel:${wa}`} className="inline-flex items-center justify-center rounded-xl border border-border bg-white py-3" data-testid={`today-call-${b.id}`} aria-label="Call">
                        <Phone className="h-4 w-4" />
                      </a>
                      <a href={`https://wa.me/${wa.replace("+", "")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 py-3" data-testid={`today-wa-${b.id}`} aria-label="WhatsApp">
                        <MessageCircle className="h-4 w-4" />
                      </a>
                    </>
                  ) : (
                    <div className="col-span-2 inline-flex items-center justify-center rounded-xl border border-dashed border-border text-neutral-400 text-xs py-3">No phone</div>
                  )}
                  <button
                    type="button"
                    onClick={() => setMoreTarget(b)}
                    className="inline-flex items-center justify-center rounded-xl border border-border bg-white py-3 text-neutral-600"
                    data-testid={`today-more-${b.id}`}
                    aria-label="More actions"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </button>
                </div>
              </div>
              </SwipeableRow>
            );
          })}
        </div>
      </div>

      <PickupWizard booking={pickupTarget} onClose={() => setPickupTarget(null)} onDone={() => { setPickupTarget(null); load(); }} />
      <ReturnWizard booking={returnTarget} onClose={() => setReturnTarget(null)} onDone={() => { setReturnTarget(null); load(); }} />
      <PaymentsSheet booking={payTarget} onClose={() => setPayTarget(null)} />
      {/* Iter 99 — per-booking More actions sheet (also reachable by swiping left). */}
      <Sheet open={!!moreTarget} onOpenChange={(o) => !o && setMoreTarget(null)}>
        <SheetContent side="bottom" className="rounded-t-3xl" data-testid="today-more-sheet">
          <SheetHeader className="mb-2">
            <SheetTitle className="text-left">
              #{(moreTarget?.id || "").slice(0, 8).toUpperCase()} — {moreTarget?.car?.make} {moreTarget?.car?.model}
            </SheetTitle>
          </SheetHeader>
          <div className="grid grid-cols-2 gap-2.5 pb-2">
            <button type="button" onClick={() => { navigate(`/agreements/${moreTarget.id}`); }} className="flex items-center gap-2.5 rounded-2xl border border-border p-3.5 text-sm font-bold" data-testid="more-view">
              <Eye className="h-4 w-4" /> View reservation
            </button>
            <button type="button" onClick={() => { setPayTarget(moreTarget); setMoreTarget(null); }} className="flex items-center gap-2.5 rounded-2xl border border-border p-3.5 text-sm font-bold" data-testid="more-payments">
              <Wallet className="h-4 w-4" /> Payments
            </button>
            <button type="button" onClick={() => { window.open(`${API}/bookings/${moreTarget.id}/agreement.pdf`, "_blank"); }} className="flex items-center gap-2.5 rounded-2xl border border-border p-3.5 text-sm font-bold" data-testid="more-pdf">
              <FileDown className="h-4 w-4" /> Agreement PDF
            </button>
            {String(moreTarget?.customer?.whatsapp || "") && (
              <a href={`https://wa.me/${String(moreTarget.customer.whatsapp).replace(/[^\d]/g, "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 rounded-2xl border border-emerald-200 bg-emerald-50 text-emerald-700 p-3.5 text-sm font-bold" data-testid="more-whatsapp">
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </a>
            )}
          </div>
        </SheetContent>
      </Sheet>
      <OpsBottomNav role={role} active="today-ops" onNavigate={handleBottomNav} />
      <div className="h-16 md:hidden" />
    </div>
  );
}
